/**
 * /learn/programs/:programId — learner-facing program detail.
 *
 * Reuses PreviewWelcomeHero from the trainer's /programs/:id/preview flow so
 * the visual treatment matches across both. Days grid below uses
 * PreviewTileGrid + PreviewTile; data is currently stubbed because the
 * /me/programs/:id detail endpoint isn't shipped yet (see useProgramDays).
 */

import React, { useMemo } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import CommentsSection from '../../components/comments/CommentsSection';
import { motion } from 'framer-motion';
import { CheckBadgeIcon } from '@heroicons/react/24/solid';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { slideUp } from '@/lib/motion';
import PreviewWelcomeHero from '../../components/programs/preview/PreviewWelcomeHero';
import LearnerCourseProgressBar from '../../components/learner/LearnerCourseProgressBar';
import type { ProgramWithProgress } from '../../components/learner/ProgramCard';
import StickyProgressRow from '../../components/learner/StickyProgressRow';
import PreviewTile from '../../components/programs/preview/PreviewTile';
import PreviewTileGrid from '../../components/programs/preview/PreviewTileGrid';
import { useImageUrl } from '../../services/uploads';
import { usePrograms } from '../../hooks/learner/usePrograms';
import { useProgramDays, PROGRAM_DAYS_API_GAP } from '../../hooks/learner/useProgramDays';

const ProgramDetailPage: React.FC = () => {
  const { programId } = useParams<{ programId: string }>();
  const navigate = useNavigate();
  const { programs, isLoading } = usePrograms();

  const program = programs.find((p) => p.id === programId);
  const [coverUrl] = useImageUrl(program?.header_image ?? null);
  const progress = (program as ProgramWithProgress | undefined)?.progress;

  const { data: days = [], isLoading: daysLoading } = useProgramDays(
    programId,
    program?.duration_days ?? undefined
  );

  // Hook must run unconditionally (before the early returns below).
  const sortedDays = useMemo(() => days.slice().sort((a, b) => a.day_number - b.day_number), [days]);

  if (isLoading) {
    return (
      <div className="max-w-[1080px] mx-auto px-4 sm:px-6 py-8 space-y-6 animate-pulse">
        <div className="h-4 w-32 rounded bg-text-tertiary/10" />
        <div className="aspect-[16/10] rounded-[28px] bg-text-tertiary/10" />
      </div>
    );
  }

  if (!program) {
    return (
      <div className="max-w-[640px] mx-auto px-4 py-12 text-center">
        <p className="text-sm text-error-500 mb-4">We couldn't find that program.</p>
        <Link to="/learn/programs" className="text-sm font-bold text-primary-500 hover:underline">
          ← Back to Programs
        </Link>
      </div>
    );
  }

  const durationLabel =
    program.duration_days != null
      ? `${program.duration_days} ${program.duration_days === 1 ? 'day' : 'days'}`
      : undefined;
  const hasCert = program.certification_type && program.certification_type !== 'none';

  const handleStart = () => {
    const firstDay = sortedDays[0];
    if (firstDay) {
      navigate(`/learn/programs/${program.id}/days/${firstDay.id}`);
    } else {
      navigate(`/learn/programs/${program.id}/done`);
    }
  };

  return (
    <motion.div {...slideUp} className="max-w-[1080px] mx-auto px-4 sm:px-6 py-6 space-y-6">
      {/* Back link + program progress — pinned while scrolling, exactly
          like the course pages. */}
      <StickyProgressRow>
        <div className="flex flex-wrap items-center gap-x-6 gap-y-3 sm:gap-x-8 px-1">
          <Link
            to="/learn/programs"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-text-secondary hover:text-text-primary transition-colors shrink-0"
          >
            <ArrowLeftIcon className="w-4 h-4" />
            Back to Programs
          </Link>
          {progress && (
            <div className="flex-1 min-w-[220px]">
              <LearnerCourseProgressBar
                percent={progress.progress_percent ?? 0}
                modulesCompleted={progress.courses_completed ?? 0}
                modulesTotal={progress.courses_total ?? 0}
              />
            </div>
          )}
        </div>
      </StickyProgressRow>

      {/* Breadcrumb path — PageHeader style (hero header page) */}
      <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 px-1">
        <Link
          to="/learn/dashboard"
          className="body-small text-text-secondary hover:text-primary-600 dark:hover:text-primary-350 transition-colors"
        >
          Home
        </Link>
        <span className="body-small text-text-secondary">•</span>
        <Link
          to="/learn/programs"
          className="body-small text-text-secondary hover:text-primary-600 dark:hover:text-primary-350 transition-colors"
        >
          Programs
        </Link>
        <span className="body-small text-text-secondary">•</span>
        <span className="body-small text-primary-600 dark:text-primary-350 font-medium truncate max-w-[240px]">
          {program.name ?? 'Program'}
        </span>
      </nav>

      {/* Hero */}
      <PreviewWelcomeHero
        coverUrl={coverUrl ?? undefined}
        title={program.name ?? 'Welcome'}
        description={program.description ?? undefined}
        durationLabel={durationLabel}
        cta={{
          label: sortedDays.length > 0 ? 'Start the program' : 'View program',
          onClick: handleStart,
        }}
      />

      {/* Detail row */}
      <div className="flex flex-wrap items-center gap-2 px-2">
        {hasCert && (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary-500/10 text-primary-600 dark:text-primary-300 text-xs font-bold">
            <CheckBadgeIcon className="w-4 h-4" />
            Certificate of completion included
          </span>
        )}
        {program.status === 'draft' && (
          <span className="inline-flex items-center px-3 py-1 rounded-full bg-warning-500/10 text-warning-600 dark:text-warning-400 text-xs font-bold">
            Draft
          </span>
        )}
      </div>

      {/* Days grid */}
      <section className="space-y-4">
        <header className="px-2">
          <h2 className="text-xl sm:text-2xl font-extrabold text-text-primary">
            Day-by-Day Breakdown
          </h2>
          <p className="text-sm text-text-secondary mt-1">
            Each day unlocks once you finish the previous one.
          </p>
        </header>

        {PROGRAM_DAYS_API_GAP && <ApiGapBanner />}

        {daysLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(3)].map((_, i) => (
              <div
                key={i}
                className="aspect-[2/3] rounded-2xl bg-text-tertiary/10 animate-pulse"
              />
            ))}
          </div>
        ) : sortedDays.length === 0 ? (
          <div className="card p-8 text-center text-sm text-text-secondary">
            This program doesn't have any days configured yet.
          </div>
        ) : (
          <PreviewTileGrid maxColumns={3}>
            {sortedDays.map((day, i) => (
              <DayTileCell
                key={day.id}
                programId={program.id ?? ''}
                day={day}
                index={i}
              />
            ))}
          </PreviewTileGrid>
        )}
      </section>

      {/* Courses have had comments since day one; programs never did, so a
          learner on a learning path had nowhere to say anything (Aleix,
          2026-09-15 — the two must not drift apart). */}
      {programId && (
        <section className="mt-10">
          <CommentsSection targetType="program" targetId={programId} />
        </section>
      )}
    </motion.div>
  );
};

export default ProgramDetailPage;

// ─── Day tile (resolves header image inside .map) ───────────────────────────

interface DayTileCellProps {
  programId: string;
  day: { id: string; day_number: number; title: string; header_image: string | null };
  index: number;
}

function DayTileCell({ programId, day, index }: DayTileCellProps) {
  const navigate = useNavigate();
  const [headerUrl] = useImageUrl(day.header_image);
  // Cosmetic state until real progress lands: first day open, rest locked.
  const state: 'open' | 'locked' = index === 0 ? 'open' : 'locked';
  return (
    <PreviewTile
      imageUrl={headerUrl ?? undefined}
      label={day.title || `Day ${day.day_number}`}
      state={state}
      lockedScope="Day"
      entranceDelay={index * 0.05}
      onClick={() => navigate(`/learn/programs/${programId}/days/${day.id}`)}
    />
  );
}

// ─── API gap banner ──────────────────────────────────────────────────────────

function ApiGapBanner() {
  return (
    <div className="rounded-xl bg-warning-50 dark:bg-warning-950/20 border border-warning-500/20 px-4 py-3">
      <div className="flex items-center gap-2 mb-1">
        <span className="inline-block w-1.5 h-1.5 rounded-full bg-warning-500" />
        <p className="text-xs font-bold text-text-primary">Days are showing sample data</p>
      </div>
      <p className="text-xs text-text-secondary leading-relaxed">
        The learner-facing endpoint{' '}
        <code className="text-[11px] font-mono px-1 py-0.5 rounded bg-text-tertiary/10 text-text-primary">
          GET /api/v1/me/programs/:id
        </code>{' '}
        isn't live yet — the day grid uses a small placeholder fixture so the design renders.
      </p>
    </div>
  );
}
